import React from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, Palette, Wand2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { SegmentedToggle } from '@/components/segmented-toggle';
import { useSystemPageDesigns } from '../hooks/use-page-designs';
import { mapWorkspaceDesign, useWorkspacePageDesigns } from '../hooks/use-workspace-page-designs';
import { BackgroundPatternPicker } from './background-pattern-picker';
import { PageDesignPicker } from './page-design-picker';
/**
 * Shared theme + background-pattern picker with an always-visible iframe
 * preview. Used by both the dedicated /customize route on campaign detail
 * and the create-campaign flow — same UX in both places, just driven by
 * different state sources (API patches vs form state).
 *
 * Layout: side-by-side picker / preview on `lg`, stacked on mobile. The
 * picker panel scrolls internally; the preview iframe takes the remaining
 * height. The iframe key is the URL itself, so it remounts on every pick
 * for a clean re-render of the animation.
 */
export function CampaignCustomizePanel({ mode, selectedDesignId, selectedPatternSlug, randomEnabled, proUnlocked, workspaceId, onPickDesign, onPickPattern, onToggleRandom, disabled, className, }) {
    const { t } = useTranslation('campaign');
    const [tab, setTab] = React.useState('theme');
    // Resolve the active design from the loaded catalog so the pattern
    // picker can tint its mini-previews and the preview iframe URL knows
    // what to render.
    const { data: systemDesigns = [] } = useSystemPageDesigns();
    const { data: workspaceRaw = [] } = useWorkspacePageDesigns(workspaceId);
    const activeDesign = React.useMemo(() => {
        const all = [...workspaceRaw.map(mapWorkspaceDesign), ...systemDesigns];
        return all.find((d) => d.id === selectedDesignId) ?? all.find((d) => d.id === 'minimal') ?? null;
    }, [systemDesigns, workspaceRaw, selectedDesignId]);
    const themeDefaultPattern = activeDesign?.tokens.layout?.backgroundPattern ?? null;
    const previewUrl = React.useMemo(() => {
        const params = new URLSearchParams();
        if (selectedDesignId)
            params.set('design', selectedDesignId);
        if (selectedPatternSlug)
            params.set('pattern', selectedPatternSlug);
        params.set('mode', mode);
        return `/page-designs/preview?${params.toString()}`;
    }, [selectedDesignId, selectedPatternSlug, mode]);
    return (<div className={'grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] ' +
            (className ?? '')}>
            {/* Picker panel — Theme + Background tabs, scrolls internally. */}
            <Card className="flex min-h-0 flex-col">
                <CardHeader className="shrink-0 pb-3">
                    <CardTitle className="flex items-center gap-2 text-base">
                        <Palette className="h-4 w-4"/>
                        {t('customize.title', { defaultValue: 'Customize public page' })}
                    </CardTitle>
                    <CardDescription>
                        {t('customize.description', {
            defaultValue: 'Pick a theme + animated background. Saves on every tap — preview updates instantly on the right.',
        })}
                    </CardDescription>
                </CardHeader>

                <div className="flex min-h-0 flex-1 flex-col">
                    <div className="mx-6 self-start">
                        <SegmentedToggle value={tab} onValueChange={(next) => setTab(next)} layoutId="customize-theme-bg" items={[
            {
                value: 'theme',
                label: t('customize.tab_theme', { defaultValue: 'Theme' }),
                icon: <Palette className="h-3.5 w-3.5"/>,
            },
            {
                value: 'background',
                label: t('customize.tab_background', { defaultValue: 'Background' }),
                icon: <Wand2 className="h-3.5 w-3.5"/>,
            },
        ]}/>
                    </div>

                    {tab === 'theme' ? (<div className="mt-3 min-h-0 flex-1 overflow-y-auto px-6 pb-6">
                            <PageDesignPicker mode={mode} selectedId={selectedDesignId} proUnlocked={proUnlocked} workspaceId={workspaceId} disabled={disabled} onPick={onPickDesign} embedded/>
                        </div>) : (<div className="mt-3 min-h-0 flex-1 space-y-4 overflow-y-auto px-6 pb-6">
                            <RandomToggle enabled={randomEnabled} onToggle={onToggleRandom} disabled={disabled}/>
                            <div className={randomEnabled ? 'opacity-60' : undefined}>
                                <BackgroundPatternPicker selectedSlug={selectedPatternSlug} themeDefaultSlug={themeDefaultPattern} proUnlocked={proUnlocked} activeDesign={activeDesign} onPick={onPickPattern} disabled={disabled || randomEnabled}/>
                            </div>
                        </div>)}
                </div>
            </Card>

            {/* Preview panel — iframe of /page-designs/preview, mode-aware
            so showroom / classic / feedback render the right sample
            content. Re-mounts on every URL change so the animation
            restarts from frame zero. */}
            <Card className="flex min-h-0 flex-col overflow-hidden">
                <CardHeader className="shrink-0 pb-3">
                    <CardTitle className="flex items-center gap-2 text-base">
                        <Eye className="h-4 w-4"/>
                        {t('customize.preview_title', { defaultValue: 'Live preview' })}
                    </CardTitle>
                    <CardDescription>
                        {t('customize.preview_hint', {
            defaultValue: 'Real CSS, sample content. No scan is recorded — this is rendered against synthetic data.',
        })}
                    </CardDescription>
                </CardHeader>
                <CardContent className="min-h-0 flex-1 overflow-hidden p-0">
                    <iframe key={previewUrl} src={previewUrl} title={t('customize.preview_title', { defaultValue: 'Live preview' })} sandbox="allow-scripts allow-same-origin" className="h-full w-full border-0 bg-background" loading="lazy"/>
                </CardContent>
            </Card>
        </div>);
}
/** Random-pattern toggle. When enabled, the pattern grid below dims so users
 *  see what's in the rotation but can't pick (it'd be ignored anyway). */
function RandomToggle({ enabled, onToggle, disabled }) {
    const { t } = useTranslation('campaign');
    return (<label className={'flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors ' +
            (enabled ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/60')}>
            <input type="checkbox" checked={enabled} onChange={(e) => onToggle(e.target.checked)} disabled={disabled} className="mt-0.5 h-4 w-4 accent-primary"/>
            <div className="flex-1">
                <div className="text-sm font-medium">
                    {t('background_pattern.random_label', { defaultValue: 'Surprise on every scan' })}
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">
                    {t('background_pattern.random_description', {
            defaultValue: 'Pick a random animation each time the page loads. Theme palette stays the same so your brand still reads consistently.',
        })}
                </p>
            </div>
        </label>);
}
